import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Fuse from 'fuse.js';
import {
	type KeyboardEvent,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { SearchBar } from '@/components/SearchBar/SearchBar';
import {
	type FieldEntry,
	getFieldsBySection
} from '@/core/dcr/fields/registry';
import { mergeSx } from '@/utils/mergeSx';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { useTranslateLabel } from '../_hooks/useFieldLabel';

import { fieldPickerStyles as sx } from './FieldPicker.styles';

type PickerItem = { key: string; label: string };
type PickerGroup = { label: string; items: PickerItem[] };

type Props = {
	open: boolean;
	anchorEl: HTMLElement | null;
	fields: FieldEntry[];
	onClose: () => void;
	onSelect: (key: string) => void;
};

export const FieldPicker = ({
	open,
	anchorEl,
	fields,
	onClose,
	onSelect
}: Props) => {
	const { t } = useTranslation();
	const translate = useTranslateLabel();
	const { entityType } = useDcrFormContext();
	const [query, setQuery] = useState('');

	const items: PickerItem[] = useMemo(
		() => fields.map(({ key, def }) => ({ key, label: translate(def.label) })),
		[fields, translate]
	);

	const groups: PickerGroup[] = useMemo(() => {
		const keys = new Set(fields.map(({ key }) => key));
		const labelOf = new Map(items.map(item => [item.key, item.label]));
		const toItem = ({ key }: FieldEntry) => ({
			key,
			label: labelOf.get(key) ?? key
		});

		const sectionGroups = getFieldsBySection(entityType).flatMap(
			({ section, fields: sectionFields }) => {
				const sectionItems = sectionFields
					.filter(({ key, def }) => keys.has(key) && def.kind !== 'collection')
					.map(toItem);

				return sectionItems.length > 0
					? [{ label: translate(section.label), items: sectionItems }]
					: [];
			}
		);

		const collectionItems = fields
			.filter(({ def }) => def.kind === 'collection')
			.map(toItem);

		return collectionItems.length > 0
			? [
					...sectionGroups,
					{ label: t('Other Sections'), items: collectionItems }
				]
			: sectionGroups;
	}, [entityType, fields, items, t, translate]);

	const fuse = useMemo(
		() => new Fuse(items, { keys: ['label'], threshold: 0.17 }),
		[items]
	);

	const searchResults = useMemo(
		() =>
			query === ''
				? []
				: fuse
						.search(query)
						.map(result => result.item)
						.sort((a, b) => a.label.localeCompare(b.label)),
		[fuse, query]
	);

	const visibleItems =
		query === '' ? groups.flatMap(group => group.items) : searchResults;

	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	useEffect(() => {
		itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [highlightedIndex]);

	const handleQueryChange = (value: string) => {
		setQuery(value);
		setHighlightedIndex(0);
	};

	const handleSelect = (key: string) => {
		onSelect(key);
		handleQueryChange('');
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (visibleItems.length === 0) return;

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setHighlightedIndex(index =>
				Math.min(index + 1, visibleItems.length - 1)
			);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setHighlightedIndex(index => Math.max(index - 1, 0));
		} else if (event.key === 'Enter') {
			const item =
				visibleItems[Math.min(highlightedIndex, visibleItems.length - 1)];
			if (item) handleSelect(item.key);
		}
	};

	if (!open) return null;

	const renderItem = (item: PickerItem, index: number) => (
		<Box
			key={item.key}
			ref={(el: HTMLDivElement | null) => {
				itemRefs.current[index] = el;
			}}
			sx={mergeSx(sx.item, index === highlightedIndex && sx.itemHighlighted)}
			onClick={() => handleSelect(item.key)}
		>
			{item.label}
		</Box>
	);

	let flatIndex = 0;

	return (
		<ClickAwayListener
			onClickAway={event => {
				if (anchorEl?.contains(event.target as Node)) return;
				onClose();
			}}
		>
			<Popper
				open={open}
				anchorEl={anchorEl}
				placement='bottom-start'
				sx={sx.popper}
			>
				<Box sx={sx.paper}>
					<Box sx={sx.searchWrap}>
						<SearchBar
							value={query}
							placeholder={t('Search fields')}
							handleChange={e => handleQueryChange(e.target.value)}
							handleClear={() => handleQueryChange('')}
							handleEnterKeyPress={handleKeyDown}
							fullWidth
							focusOnMount
						/>
					</Box>

					<Box sx={sx.list}>
						{visibleItems.length === 0 && (
							<Box sx={sx.noResults}>{t('No matching fields')}</Box>
						)}

						{query === ''
							? groups.map(group => (
									<Box key={group.label}>
										<Box sx={sx.groupLabel}>{group.label}</Box>
										{group.items.map(item => renderItem(item, flatIndex++))}
									</Box>
								))
							: searchResults.map(renderItem)}
					</Box>
				</Box>
			</Popper>
		</ClickAwayListener>
	);
};
